import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
// @mui
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
// auth
import { useAuthContext } from '../../auth/useAuthContext';

// ----------------------------------------------------------------------

// Matches the cooldown the other Emovur apps use: long enough that a
// double-tap cannot burn the resend budget, short enough to retry a code that
// genuinely never arrived.
const RESEND_COOLDOWN_SECONDS = 30;

// Plain controlled inputs rather than the react-hook-form setup the login form
// uses: the field is a 6-digit code or a 10-character recovery code depending
// on a toggle, and describing that with one resolver costs more than it saves.
TwoFactorChallengeForm.propTypes = {
  challenge: PropTypes.shape({
    method: PropTypes.string,
    challengeToken: PropTypes.string,
    maskedEmail: PropTypes.string,
  }).isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default function TwoFactorChallengeForm({ challenge, onCancel }) {
  const { enqueueSnackbar } = useSnackbar();
  const { verifyTwoFactor, resendTwoFactorCode } = useAuthContext();

  const { method, challengeToken, maskedEmail } = challenge;

  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [usingBackupCode, setUsingBackupCode] = useState(false);
  const [emailFallbackSent, setEmailFallbackSent] = useState(method === 'email');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  // The emailed code is sent before this screen renders, so an email-method
  // challenge starts its cooldown already running.
  const [cooldown, setCooldown] = useState(method === 'email' ? RESEND_COOLDOWN_SECONDS : 0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timerId = setTimeout(() => setCooldown((value) => value - 1), 1000);
    return () => clearTimeout(timerId);
  }, [cooldown]);

  const trimmed = code.trim();
  const canSubmit = usingBackupCode
    ? trimmed.replace(/[^A-Za-z0-9]/g, '').length === 10
    : /^\d{6}$/.test(trimmed);

  // A successful verify dispatches LOGIN, and the guards take it from there —
  // the same way the password-only path works.
  const onSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError('');
    try {
      await verifyTwoFactor(challengeToken, trimmed, trustDevice);
    } catch (err) {
      setCode('');
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const onResend = async () => {
    setResending(true);
    try {
      await resendTwoFactorCode(challengeToken);
      setEmailFallbackSent(true);
      setUsingBackupCode(false);
      setCode('');
      setCooldown(RESEND_COOLDOWN_SECONDS);
      enqueueSnackbar('A new code is on its way.');
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' });
    } finally {
      setResending(false);
    }
  };

  const toggleBackupCode = () => {
    setUsingBackupCode((value) => !value);
    setCode('');
    setError('');
  };

  const prompt = () => {
    if (usingBackupCode) {
      return 'Enter one of the recovery codes you saved when you turned on two-step sign-in. Each code works once.';
    }
    if (method === 'totp' && !emailFallbackSent) {
      return 'Open your authenticator app and enter the 6-digit code it shows for this account.';
    }
    if (method === 'totp' && emailFallbackSent) {
      return `Enter the 6-digit code from your authenticator app, or the one we just sent to ${
        maskedEmail || 'your email'
      }.`;
    }
    return `Enter the 6-digit code we sent to ${
      maskedEmail || 'your email'
    }. It expires in 5 minutes.`;
  };

  return (
    <form onSubmit={onSubmit}>
      <Stack spacing={2.5}>
        <Stack spacing={1}>
          <Typography variant="h4">Two-step sign-in</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {prompt()}
          </Typography>
        </Stack>

        {!!error && <Alert severity="error">{error}</Alert>}

        <TextField
          autoFocus
          fullWidth
          autoComplete="one-time-code"
          label={usingBackupCode ? 'Recovery code' : '6-digit code'}
          value={code}
          onChange={(event) => setCode(event.target.value)}
          inputProps={
            usingBackupCode
              ? { maxLength: 11, style: { textTransform: 'uppercase' } }
              : { inputMode: 'numeric', maxLength: 6 }
          }
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={trustDevice}
              onChange={(event) => setTrustDevice(event.target.checked)}
            />
          }
          label="Don't ask again on this device for 30 days"
        />

        <LoadingButton
          fullWidth
          color="inherit"
          size="large"
          type="submit"
          variant="contained"
          loading={submitting}
          disabled={!canSubmit}
        >
          Verify
        </LoadingButton>

        <Stack spacing={1} alignItems="center">
          {!usingBackupCode && (
            <LoadingButton
              size="small"
              onClick={onResend}
              loading={resending}
              disabled={cooldown > 0}
            >
              {cooldown > 0 ? `Send a code to my email in ${cooldown}s` : 'Send a code to my email'}
            </LoadingButton>
          )}

          <Link
            component="button"
            type="button"
            variant="body2"
            underline="always"
            onClick={toggleBackupCode}
            sx={{ cursor: 'pointer' }}
          >
            {usingBackupCode ? 'Enter a 6-digit code instead' : 'Use a recovery code instead'}
          </Link>

          <Button size="small" onClick={onCancel}>
            Back to sign in
          </Button>
        </Stack>
      </Stack>
    </form>
  );
}
